import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Typography,
  Grid,
  Button,
  Chip
} from '@mui/material';
import {
    Restore as RestoreIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getServiceName } from '../../utils/serviceTranslations';
import { getRoleName } from '../../utils/roleTranslations';
import { getPermissionName, groupPermissionsByCategory, orderedPermissionCategoryEntries } from '../../utils/permissionTranslations';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import {
  ServiceValueMode,
  formatServiceDurationLabel,
  formatServicePriceLabel,
  normalizeServiceValueMode,
} from '../../utils/serviceValueFormat';
import RoleService from '../../services/roleService';
import api from '../../services/api';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { BRAND } from '../../theme/brandTokens';

const TeamMemberReview = ({
  formData,
  currentStore,
  services,
  onRollback,
  showRollback,
  loading,
  saving,
  isUpdate = false,
  isExistingMember = false,
  existingMemberName = '',
  billingImpact = null,
  savedAcceptsCustomerBookings = true,
}) => {
  const { t, i18n } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissionsTranslations, setPermissionsTranslations] = useState({});
  const [rolePermissions, setRolePermissions] = useState([]);

  useEffect(() => {
    if (!currentStore?.id) return undefined;

    const requestKey = `getRoles-${currentStore.id}`;
    let cancelled = false;

    const loadData = async () => {
      try {
        const rolesData = await requestDeduplicator.deduplicate(requestKey, async () => {
          return await RoleService.getRolesByStore();
        });

        if (!cancelled) {
          setRoles(rolesData || []);

          if (formData.roleId) {
            const role = rolesData?.find(r => r.id === formData.roleId);
            if (role) {
              if (Array.isArray(role.permissions)) {
                setRolePermissions(role.permissions);
              } else if (typeof role.permissions === 'object' && role.permissions !== null) {
                setRolePermissions(Object.keys(role.permissions));
              }
            }
          }
        }

        const permissionsKey = 'getAllPermissions';
        const permissionsData = await requestDeduplicator.deduplicate(permissionsKey, async () => {
          const response = await api.get('/permissions');
          return response.data;
        });

        if (!cancelled) {
          setPermissionsTranslations(permissionsData || {});
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load roles or permissions:', error);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [currentStore?.id, formData.roleId]);

  const getRoleNameForDisplay = () => {
    if (!formData.roleId) return t('common.notProvided');
    const role = roles.find(r => r.id === formData.roleId);
    return role ? getRoleName(role.name, i18n.language) : t('common.notProvided');
  };

  const getPermissionLabel = (permission) => {
    if (permissionsTranslations[permission]) {
      return getPermissionName(permissionsTranslations[permission], i18n.language);
    }
    return permission;
  };

  const step4TitleKey = isUpdate ? 'teamMembers.update.steps.step4.title' : 'teamMembers.registration.steps.step4.title';
  const step4DescriptionKey = isUpdate ? 'teamMembers.update.steps.step4.description' : 'teamMembers.registration.steps.step4.description';

  const getSelectedServiceDetails = () => {
    return (formData.services || [])
      .map((selectedService) => {
        const service = services.find((s) => s.id === selectedService.serviceId);
        if (!service) {
          return null;
        }

        return {
          ...service,
          customPrice: selectedService.customPrice
        };
      })
      .filter(Boolean);
  };

  const selectedServices = getSelectedServiceDetails();
  const currencyCode = (currentStore && currentStore.currency) ? currentStore.currency : DEFAULT_CURRENCY;

  const willIncurAdditionalCharge = Boolean(
    billingImpact?.willIncurAdditionalCharge ?? billingImpact?.WillIncurAdditionalCharge
  );
  const isTrialBilling = Boolean(billingImpact?.isTrial ?? billingImpact?.IsTrial);
  const billingCurrency = (
    billingImpact?.currency
    ?? billingImpact?.Currency
    ?? currencyCode
    ?? 'EUR'
  ).toUpperCase();
  const recurringIncrease = billingImpact?.recurringMonthlyIncrease
    ?? billingImpact?.RecurringMonthlyIncrease
    ?? billingImpact?.additionalSeatUnitAmount
    ?? billingImpact?.AdditionalSeatUnitAmount
    ?? 0;
  const billingPriceLabel = formatCurrency(recurringIncrease, billingCurrency);
  const stoppedTakingBookings = isUpdate
    && savedAcceptsCustomerBookings !== false
    && formData.acceptsCustomerBookings === false;
  const startedTakingBookings = isUpdate
    && savedAcceptsCustomerBookings === false
    && formData.acceptsCustomerBookings !== false;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ color: BRAND.text, fontWeight: 600 }}>
          {t(step4TitleKey)}
        </Typography>
        {showRollback && onRollback && (
          <Button
            variant="outlined"
            size="small"
            onClick={onRollback}
            disabled={loading || saving}
            startIcon={<RestoreIcon />}
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'center' },
              borderRadius: 2,
              px: 1.5,
              minHeight: 40,
              whiteSpace: 'nowrap',
              borderColor: '#9e9e9e',
              color: '#616161',
              fontWeight: 500,
              fontSize: '0.8125rem',
              '&:hover': {
                borderColor: '#757575',
                backgroundColor: 'rgba(158, 158, 158, 0.08)',
                color: '#424242',
              },
              '&:disabled': {
                borderColor: 'rgba(158, 158, 158, 0.3)',
                color: 'rgba(158, 158, 158, 0.5)',
              }
            }}
          >
            {t('teamMembers.update.discardChanges')}
          </Button>
        )}
      </Box>
      <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
        {t(step4DescriptionKey)}
      </Typography>

      {stoppedTakingBookings && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('teamMembers.acceptsCustomerBookings.currentPeriodNotice')}
        </Alert>
      )}

      {startedTakingBookings && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('teamMembers.acceptsCustomerBookings.chargeNotice')}
        </Alert>
      )}

      {willIncurAdditionalCharge && (
        <Alert
          severity={isTrialBilling ? 'info' : 'warning'}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('teamMembers.billing.confirmTitle')}
          </Typography>
          <Typography variant="body2">
            {isTrialBilling
              ? t('teamMembers.billing.trialNotice', { price: billingPriceLabel })
              : t('teamMembers.billing.reviewChargeNotice', { price: billingPriceLabel })}
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Personal Information */}
        <Box sx={{ p: 3, border: `1px solid ${BRAND.line}`, borderRadius: '12px' }}>
          <Typography variant="h6" sx={{ mb: 2, color: BRAND.text }}>
            {t('teamMembers.registration.steps.step1.title')}
          </Typography>
          {isExistingMember ? (
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.name')}</Typography>
                  <Typography variant="body1">{existingMemberName || formData.name}</Typography>
                </Box>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.email')}</Typography>
                  <Typography variant="body1">{formData.email}</Typography>
                </Box>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.jobTitle')}</Typography>
                  <Typography variant="body1">{formData.jobTitle || t('common.notProvided')}</Typography>
                </Box>
              </Grid>
            </Grid>
          ) : (
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.name')}</Typography>
                <Typography variant="body1">{formData.name}</Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.email')}</Typography>
                <Typography variant="body1">{formData.email}</Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.jobTitle')}</Typography>
                <Typography variant="body1">{formData.jobTitle || t('common.notProvided')}</Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.phone')}</Typography>
                <Typography variant="body1">{formData.phoneNumber ? `${formData.phoneCountryCode}${formData.phoneNumber}` : t('common.notProvided')}</Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.genre')}</Typography>
                <Typography variant="body1">{formData.genre ? t(`teamMembers.registration.${formData.genre}`) : t('common.notProvided')}</Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.birthDate')}</Typography>
                <Typography variant="body1">
                  {formData.birthDate
                    ? (() => {
                        const birthDateObj = formData.birthDate instanceof Date 
                          ? formData.birthDate 
                          : new Date(formData.birthDate);
                        return birthDateObj.toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        });
                      })()
                    : t('common.notProvided')
                  }
                </Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.language')}</Typography>
                <Typography variant="body1">
                  {t(`common.languages.${formData.language}`)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          )}
        </Box>

        {/* Store Assignment */}
        <Box sx={{ p: 3, border: `1px solid ${BRAND.line}`, borderRadius: '12px' }}>
          <Typography variant="h6" sx={{ mb: 2, color: BRAND.text }}>
            {t('teamMembers.registration.storeAssignment')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.currentStore')}</Typography>
                <Typography variant="body1">{currentStore?.name}</Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.registration.role')}</Typography>
                <Typography variant="body1">{getRoleNameForDisplay()}</Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{t('teamMembers.acceptsCustomerBookings.label')}</Typography>
                <Typography variant="body1">
                  {formData.acceptsCustomerBookings !== false ? t('common.yes') : t('common.no')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Role & Permissions */}
        <Box sx={{ p: 3, border: `1px solid ${BRAND.line}`, borderRadius: '12px', mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: BRAND.text }}>
            {t('teamMembers.registration.steps.step2.title')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('teamMembers.registration.role')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {getRoleNameForDisplay()}
                </Typography>
              </Box>
            </Grid>
            {(rolePermissions.length > 0 || (formData.additionalPermissions && formData.additionalPermissions.length > 0)) && (
              <Grid item size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('teamMembers.registration.permissions')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {rolePermissions.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('teamMembers.registration.fromRole')}:
                      </Typography>
                      {orderedPermissionCategoryEntries(groupPermissionsByCategory(rolePermissions)).map(([category, perms]) => (
                        <Box key={category} sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                            {t(`roles.modal.form.categories.${category}`)}:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {perms.map(perm => (
                              <Chip
                                key={perm}
                                label={getPermissionLabel(perm)}
                                size="small"
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  fontSize: '0.75rem'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {formData.additionalPermissions && formData.additionalPermissions.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('teamMembers.registration.additional')}:
                      </Typography>
                      {orderedPermissionCategoryEntries(groupPermissionsByCategory(formData.additionalPermissions)).map(([category, perms]) => (
                        <Box key={category} sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                            {t(`roles.modal.form.categories.${category}`)}:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {perms.map(perm => (
                              <Chip
                                key={perm}
                                label={getPermissionLabel(perm)}
                                size="small"
                                sx={{
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  fontSize: '0.75rem'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Services */}
        <Box sx={{ p: 3, border: `1px solid ${BRAND.line}`, borderRadius: '12px' }}>
          <Typography variant="h6" sx={{ mb: 2, color: BRAND.text }}>
            {t('teamMembers.registration.steps.step3.title')}
          </Typography>
          {selectedServices.length === 0 ? (
            <Typography variant="body1" color="text.secondary">{t('teamMembers.registration.noServicesSelected')}</Typography>
          ) : (
            <Grid container spacing={2}>
              {selectedServices.map((service) => (
                <Grid item xs={12} sm={6} md={4} key={service.id}>
                  <Box
                    sx={{
                      p: 2,
                      border: `1px solid ${BRAND.line}`,
                      borderRadius: 1,
                      backgroundColor: '#fff'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {getServiceName(service.name, i18n.language)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t('teamMembers.registration.price')}:</strong> {normalizeServiceValueMode(service.priceMode) === ServiceValueMode.Fixed && service.customPrice != null && service.customPrice !== ''
                        ? formatCurrency(service.customPrice, currencyCode)
                        : formatServicePriceLabel(service, {
                          formatMoney: (amount) => formatCurrency(amount, currencyCode),
                          onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                          freeLabel: t('servicesManagement.card.free'),
                          fromLabel: (price) => t('servicesManagement.valueMode.priceFrom', { price }),
                        })}
                      {service.customPrice != null && service.customPrice !== '' && (
                        <span style={{ color: BRAND.accentText, marginLeft: '4px' }}>
                          ({t('teamMembers.registration.custom')})
                        </span>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t('teamMembers.registration.duration')}:</strong> {formatServiceDurationLabel(service, {
                        onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                      })}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TeamMemberReview;
